import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import { Button, Card, CardContent, Chip, Link, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useContactmomentenVan, useLaatsteContactmoment } from '../../api/contactmomenten.ts';
import { useKandidaat, useKandidaatMutaties } from '../../api/kandidaten.ts';
import { useMatchesVoorKandidaat } from '../../api/matches.ts';
import type { KandidaatProfiel } from '../../api/typen.ts';
import { useVoordrachtenVanKandidaat } from '../../api/voordrachten.ts';
import { useSessie } from '../../auth/SessieContext.tsx';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { ContactmomentDialoog } from '../../componenten/ContactmomentDialoog.tsx';
import { ContactmomentenLijst } from '../../componenten/ContactmomentenLijst.tsx';
import { Kenmerken } from '../../componenten/Kenmerken.tsx';
import { locatieLabel } from '../../componenten/keuzevelden.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { MatchesTabel } from '../../componenten/MatchesTabel.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { VoordrachtDialoog } from '../../componenten/VoordrachtDialoog.tsx';
import { VoordrachtenTabel } from '../../componenten/VoordrachtenTabel.tsx';
import { datum, datumTijd, ofStreepje, opsomming } from '../../helpers/formatteer.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { CvsPaneel } from './CvsPaneel.tsx';
import { KandidaatFormulier } from './KandidaatFormulier.tsx';
import { KandidaatProfielTab } from './KandidaatProfielTab.tsx';

const tabs = ['profiel', 'cvs', 'voordrachten', 'matches', 'gesprekken'] as const;
type Tabnaam = (typeof tabs)[number];

function Gegevens({ profiel }: { profiel: KandidaatProfiel }) {
  const laatste = useLaatsteContactmoment(profiel.id);
  return (
    <Kenmerken
      kolommen={3}
      items={[
        {
          label: 'E-mailadres',
          waarde: profiel.email ? <Link href={`mailto:${profiel.email}`}>{profiel.email}</Link> : null,
        },
        {
          label: 'Telefoon',
          waarde: profiel.telefoon ? <Link href={`tel:${profiel.telefoon}`}>{profiel.telefoon}</Link> : null,
        },
        {
          label: 'LinkedIn',
          waarde: profiel.linkedinUrl ? (
            <Link href={profiel.linkedinUrl} target="_blank" rel="noreferrer">
              Profiel openen
            </Link>
          ) : null,
        },
        {
          label: 'Geboortedatum',
          waarde: profiel.geboortedatum ? `${datum(profiel.geboortedatum)} (${profiel.leeftijd} jaar)` : null,
        },
        {
          label: 'Adres',
          waarde: [profiel.straatHuisnummer, profiel.locatie ? locatieLabel(profiel.locatie) : null]
            .filter(Boolean)
            .join(', '),
        },
        { label: 'Rijbewijzen', waarde: opsomming(profiel.rijbewijzen) },
        { label: 'Bron', waarde: ofStreepje(profiel.bron) },
        {
          label: 'Laatste contact',
          waarde: laatste.data ? datumTijd(laatste.data.tijdstip) : laatste.isPending ? '…' : 'Nog geen contact',
        },
        { label: 'Geregistreerd', waarde: datum(profiel.aangemaaktOp) },
      ]}
    />
  );
}

function GesprekkenTab({ profiel }: { profiel: KandidaatProfiel }) {
  const { offset, limiet, zetOffset } = useZoekparameters(25);
  const gesprekken = useContactmomentenVan('kandidaat', profiel.id, { limiet, offset });
  return (
    <ContactmomentenLijst
      query={gesprekken}
      toon={{ kandidaat: false }}
      paginering={{ offset, limiet, onOffset: zetOffset }}
    />
  );
}

function VoordrachtenTab({ profiel }: { profiel: KandidaatProfiel }) {
  const voordrachten = useVoordrachtenVanKandidaat(profiel.id);
  return (
    <Laadstatus query={voordrachten} leeg="Deze kandidaat is nog niet voorgedragen.">
      {(lijst) => <VoordrachtenTabel voordrachten={lijst} toon="vacature" />}
    </Laadstatus>
  );
}

function MatchesTab({ profiel }: { profiel: KandidaatProfiel }) {
  const matches = useMatchesVoorKandidaat(profiel.id, { limiet: 100 });
  return (
    <Laadstatus query={matches} leeg="Nog geen matches berekend voor deze kandidaat.">
      {(lijst) => <MatchesTabel matches={lijst} vast={{ kandidaat: profiel }} />}
    </Laadstatus>
  );
}

/**
 * De pagina van een kandidaat: gegevens, profiel, cv's, voordrachten, matches en gesprekken.
 * @returns De pagina.
 */
export function KandidaatPagina() {
  const { id = '' } = useParams();
  const profiel = useKandidaat(id);
  const { krijg, zet } = useZoekparameters();
  const tab = (tabs as readonly string[]).includes(krijg('tab')) ? (krijg('tab') as Tabnaam) : 'profiel';
  const { isBeheerder } = useSessie();
  const mutaties = useKandidaatMutaties();
  const { meld, meldFout } = useMeldingen();
  const navigeer = useNavigate();
  const [bewerken, setBewerken] = useState(false);
  const [gesprek, setGesprek] = useState(false);
  const [voordragen, setVoordragen] = useState(false);
  const [verwijderen, setVerwijderen] = useState(false);

  return (
    <Laadstatus query={profiel}>
      {(kandidaat) => (
        <Pagina
          titel={kandidaat.volledigeNaam}
          terug={{ naar: '/kandidaten', label: 'Kandidaten' }}
          ondertitel={
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
              <StatusChip soort="kandidaat" waarde={kandidaat.status} />
              {kandidaat.talentpools.map((k) => (
                <Chip key={k.talentpoolId} label={k.talentpool.naam} size="small" variant="outlined" color="primary" />
              ))}
              {kandidaat.functietitels.map((k) => (
                <Chip key={k.functietitelId} label={k.functietitel.naam} size="small" variant="outlined" />
              ))}
            </Stack>
          }
          acties={
            <>
              <Button variant="outlined" startIcon={<ForumIcon />} onClick={() => setGesprek(true)}>
                Gesprek vastleggen
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setVoordragen(true)}
                disabled={kandidaat.status === 'inactief'}
              >
                Voordragen
              </Button>
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setBewerken(true)}>
                Bewerken
              </Button>
              {isBeheerder && (
                <Button color="error" startIcon={<DeleteOutlinedIcon />} onClick={() => setVerwijderen(true)}>
                  Verwijderen
                </Button>
              )}
            </>
          }
        >
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Gegevens profiel={kandidaat} />
            </CardContent>
          </Card>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_gebeurtenis, waarde: Tabnaam) => zet({ tab: waarde === 'profiel' ? undefined : waarde })}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab value="profiel" label="Profiel" />
              <Tab value="cvs" label={`Cv's (${kandidaat.cvs.length})`} />
              <Tab value="voordrachten" label="Voordrachten" />
              <Tab value="matches" label="Matches" />
              <Tab value="gesprekken" label="Gesprekken" />
            </Tabs>
          </Paper>
          {tab === 'profiel' && <KandidaatProfielTab profiel={kandidaat} />}
          {tab === 'cvs' && (
            <Paper sx={{ p: 2 }}>
              <CvsPaneel kandidaat={kandidaat} />
            </Paper>
          )}
          {tab === 'voordrachten' && (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Voordrachten</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setVoordragen(true)}
                  disabled={kandidaat.status === 'inactief'}
                >
                  Voordragen
                </Button>
              </Stack>
              <VoordrachtenTab profiel={kandidaat} />
            </Paper>
          )}
          {tab === 'matches' && (
            <Paper sx={{ p: 2 }}>
              <MatchesTab profiel={kandidaat} />
            </Paper>
          )}
          {tab === 'gesprekken' && (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Gesprekken</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setGesprek(true)}>
                  Gesprek vastleggen
                </Button>
              </Stack>
              <GesprekkenTab profiel={kandidaat} />
            </Paper>
          )}

          <KandidaatFormulier open={bewerken} onSluit={() => setBewerken(false)} bestaand={kandidaat} />
          <ContactmomentDialoog open={gesprek} onSluit={() => setGesprek(false)} kandidaat={kandidaat} />
          <VoordrachtDialoog
            open={voordragen}
            onSluit={() => setVoordragen(false)}
            kandidaat={kandidaat}
            onGemaakt={(voordracht) => void navigeer(`/voordrachten/${voordracht.id}`)}
          />
          <BevestigDialoog
            open={verwijderen}
            titel="Kandidaat definitief verwijderen"
            bevestigLabel="Definitief verwijderen"
            gevaarlijk
            bezig={mutaties.verwijder.isPending}
            onSluit={() => setVerwijderen(false)}
            onBevestig={() =>
              mutaties.verwijder
                .mutateAsync(kandidaat.id)
                .then(() => {
                  meld('Kandidaat verwijderd');
                  void navigeer('/kandidaten');
                })
                .catch(meldFout)
            }
          >
            Alle gegevens van {kandidaat.volledigeNaam} worden verwijderd, inclusief cv-bestanden, voordrachten en
            gesprekken. Dit is bedoeld voor een verzoek tot verwijdering onder de AVG en kan niet ongedaan worden
            gemaakt.
          </BevestigDialoog>
        </Pagina>
      )}
    </Laadstatus>
  );
}
